// ─────────────────────────────────────────────────────────────────────────────
// ROXXE ARTWORK — golden-red Dakota Sport Retriever, heavy-lidded side-eye,
// one raised eyebrow, smirk, red collar with a gold tag.
// ─────────────────────────────────────────────────────────────────────────────

export const C = {
  coat:  "#c47a35",
  light: "#e2a55c",
  shade: "#9a5522",
  ear:   "#87471b",
  paw:   "#d8914a",
  nose:  "#2b1b14",
  iris:  "#5b3414",
  pupil: "#170c05",
  line:  "#56290d",
  collar:"#c8372d",
  tag:   "#f2b640",
};

// Head + collar, drawn in the 200×224 "awake" coordinate space.
// lidClass animates the blink; omit it for static uses (logo).
function DogHead({ lidClass }) {
  const eye = (cx) => (
    <g key={cx}>
      <ellipse cx={cx} cy="78" rx="8.5" ry="8" fill="#2a170a"/>
      <circle cx={cx + 1.8} cy="78.5" r="6.4" fill={C.iris}/>
      <circle cx={cx + 3.2} cy="78.5" r="3.7" fill={C.pupil}/>
      <circle cx={cx + 4.6} cy="76" r="1.7" fill="white" opacity="0.9"/>
      {/* permanent heavy upper lid = unimpressed */}
      <path d={`M ${cx - 10},75.5 C ${cx - 6},72.2 ${cx + 6},72.2 ${cx + 10},75.5 L ${cx + 10},67 L ${cx - 10},67 Z`} fill={C.coat}/>
      <path d={`M ${cx - 9},75.2 C ${cx - 5},72.4 ${cx + 5},72.4 ${cx + 9},75.2`} stroke={C.line} strokeWidth="1.4" fill="none" strokeLinecap="round"/>
      {lidClass && (
        <ellipse cx={cx} cy="78" rx="10" ry="9.5" fill={C.coat} className={lidClass}
          style={{ transformOrigin: `${cx}px 68.5px` }}/>
      )}
    </g>
  );
  return (
    <g>
      {/* Collar (behind head so the chin overlaps it) */}
      <path d="M 66,114 C 80,132 120,132 134,114" stroke={C.collar} strokeWidth="7" fill="none" strokeLinecap="round"/>
      <line x1="100" y1="128" x2="100" y2="133" stroke={C.tag} strokeWidth="1.6"/>
      <circle cx="100" cy="138" r="6.2" fill={C.tag} stroke="#a8721a" strokeWidth="1.2"/>
      <text x="100" y="141" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#7a4d0c" fontFamily="Georgia,serif">R</text>

      {/* Skull */}
      <ellipse cx="100" cy="76" rx="42" ry="38" fill={C.coat}/>
      <ellipse cx="100" cy="57" rx="21" ry="12" fill={C.light} opacity="0.4"/>

      {/* Floppy ears */}
      <path d="M 66,50 C 46,52 38,86 46,114 C 52,124 64,118 68,104 C 72,88 74,64 66,50 Z" fill={C.ear}/>
      <path d="M 134,50 C 154,52 162,86 154,114 C 148,124 136,118 132,104 C 128,88 126,64 134,50 Z" fill={C.ear}/>
      <path d="M 60,62 C 52,78 50,96 54,110" stroke="#6e3712" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.6"/>
      <path d="M 140,62 C 148,78 150,96 146,110" stroke="#6e3712" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.6"/>

      {/* Muzzle */}
      <ellipse cx="100" cy="106" rx="25" ry="19" fill={C.light}/>

      {eye(82)}
      {eye(118)}

      {/* Brows — left one raised. The side-eye is the whole brand. */}
      <path d="M 71,63 Q 81,55 92,62" stroke={C.line} strokeWidth="2.8" fill="none" strokeLinecap="round"/>
      <path d="M 108,66.5 Q 118,64.5 129,66.5" stroke={C.line} strokeWidth="2.8" fill="none" strokeLinecap="round"/>

      {/* Nose */}
      <path d="M 90,97 Q 100,92 110,97 Q 109,106 100,109 Q 91,106 90,97 Z" fill={C.nose}/>
      <ellipse cx="96" cy="97.5" rx="3.6" ry="1.8" fill="white" opacity="0.3"/>

      {/* Smirk — right side curls up */}
      <line x1="100" y1="109" x2="100" y2="115" stroke={C.line} strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M 100,115 C 96,119 90,119 86,116.5" stroke={C.line} strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M 100,115 C 106,118.5 113,116.5 117,110.5" stroke={C.line} strokeWidth="2" fill="none" strokeLinecap="round"/>
    </g>
  );
}

// ── Awake: sitting up, looking at you like you owe her money ──
export function RoxxeAwake({ eyeState }) {
  const lidClass = eyeState === "closed" ? "lid-closed"
    : eyeState === "opening" ? "lid-opening"
    : "lid-blinking";
  return (
    <svg viewBox="0 0 200 224" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      {/* Tail */}
      <g className="tail-wag" style={{ transformOrigin: "146px 200px" }}>
        <path d="M 146,200 C 176,198 190,176 182,150" stroke={C.coat} strokeWidth="13" fill="none" strokeLinecap="round"/>
        <path d="M 152,203 C 180,200 195,178 188,154" stroke={C.light} strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.6"/>
      </g>

      {/* Haunches + body */}
      <ellipse cx="100" cy="192" rx="58" ry="30" fill={C.shade}/>
      <path d="M 64,118 C 54,150 58,186 74,204 L 126,204 C 142,186 146,150 136,118 Z" fill={C.coat}/>

      {/* Chest feathering */}
      <path d="M 82,124 C 76,150 80,178 100,190 C 120,178 124,150 118,124 Z" fill={C.light}/>
      <path d="M 92,186 L 95,195 M 100,188 L 100,198 M 108,186 L 105,195" stroke={C.light} strokeWidth="3" strokeLinecap="round"/>

      {/* Front legs + paws */}
      <rect x="75" y="150" width="17" height="58" rx="8" fill={C.coat}/>
      <rect x="108" y="150" width="17" height="58" rx="8" fill={C.coat}/>
      <path d="M 90,158 L 90,202" stroke={C.shade} strokeWidth="1.4" opacity="0.5"/>
      <path d="M 110,158 L 110,202" stroke={C.shade} strokeWidth="1.4" opacity="0.5"/>
      <ellipse cx="83.5" cy="209" rx="14" ry="8" fill={C.paw}/>
      <ellipse cx="116.5" cy="209" rx="14" ry="8" fill={C.paw}/>
      {[77.5, 83.5, 89.5, 110.5, 116.5, 122.5].map((x) => (
        <line key={x} x1={x} y1="205" x2={x} y2="213" stroke={C.line} strokeWidth="1.1" strokeLinecap="round" opacity="0.45"/>
      ))}

      <DogHead lidClass={lidClass}/>
    </svg>
  );
}

// ── Asleep: curled up, still somehow judging you ──
export function RoxxeSleep() {
  return (
    <svg viewBox="0 0 240 172" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <text x="44" y="74" className="zz z1" fill="#f4a93c" fontSize="12" fontFamily="'IBM Plex Mono',monospace">z</text>
      <text x="56" y="58" className="zz z2" fill="#f4a93c" fontSize="15" fontFamily="'IBM Plex Mono',monospace">z</text>
      <text x="70" y="40" className="zz z3" fill="#f4a93c" fontSize="18" fontFamily="'IBM Plex Mono',monospace">Z</text>

      {/* Body */}
      <ellipse cx="130" cy="104" rx="86" ry="50" fill={C.coat}/>
      <path d="M 64,72 C 102,50 162,50 204,78" stroke={C.light} strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.45"/>
      <ellipse cx="180" cy="100" rx="40" ry="38" fill={C.shade} opacity="0.55"/>
      <ellipse cx="198" cy="142" rx="16" ry="8" fill={C.paw}/>

      {/* Tail wrapped around the front */}
      <path d="M 208,112 C 216,142 182,162 132,162 C 112,162 100,160 92,158" stroke={C.coat} strokeWidth="14" fill="none" strokeLinecap="round"/>
      <path d="M 206,122 C 210,148 180,166 132,166 C 114,166 104,164 96,163" stroke={C.light} strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.6"/>

      {/* Head resting on paws */}
      <ellipse cx="72" cy="112" rx="36" ry="29" fill={C.coat}/>
      <ellipse cx="44" cy="124" rx="24" ry="14" fill={C.light}/>
      <ellipse cx="22" cy="121" rx="6.5" ry="4.8" fill={C.nose}/>
      <ellipse cx="20" cy="119.5" rx="2" ry="1" fill="white" opacity="0.3"/>
      <path d="M 26,130 C 32,133 40,133 46,131" stroke={C.line} strokeWidth="1.6" fill="none" strokeLinecap="round"/>

      {/* Front paws */}
      <ellipse cx="46" cy="144" rx="22" ry="8" fill={C.paw}/>
      <ellipse cx="78" cy="148" rx="18" ry="7.5" fill={C.paw}/>
      {[36, 44, 52, 72, 80].map((x, i) => (
        <line key={x} x1={x} y1={i < 3 ? 140 : 145} x2={x} y2={i < 3 ? 148 : 152} stroke={C.line} strokeWidth="1" strokeLinecap="round" opacity="0.45"/>
      ))}

      {/* Floppy ear */}
      <path d="M 76,88 C 98,86 106,112 96,132 C 88,140 76,128 72,110 Z" fill={C.ear}/>

      {/* Closed eye + one brow still raised */}
      <path d="M 50,108 Q 57,113 64,108" stroke={C.line} strokeWidth="2.4" fill="none" strokeLinecap="round"/>
      <path d="M 48,99 Q 56,93 64,98" stroke={C.line} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
    </svg>
  );
}

// ── Header logo: just the head ──
export function RoxxeLogo({ size = 60 }) {
  return (
    <svg viewBox="36 34 128 114" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: size * 114 / 128, flexShrink: 0 }}>
      <DogHead/>
    </svg>
  );
}
